import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { Menu, Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'react-toastify';
import { generateInvoiceNumber, generateInvoicePDF } from '../utils/invoiceUtils';
import CreateDelivery from '../components/CreateDelivery';
import MaterialDrawer from '../components/MaterialDrawer';
import KanbanJob from './KanbanJob';


function Job() {

  const navigate = useNavigate(); // Add this line at the start of the Job component
  const { user } = useAuth();
  const { po_number } = useParams();
  const [jobsData, setJobsData] = useState([]);
  const [isKanban, setIsKanban] = useState(false);
  const [isKanbanOnly, setIsKanbanOnly] = useState(false);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [deliveriesData, setDeliveriesData] = useState([]);
  const [productionLogs, setProductionLogs] = useState([]);
  const [shops, setShops] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(null);
  const [newDelivery, setNewDelivery] = useState({
    items: [],
    ship_date: new Date().toISOString().split('T')[0],
    tracking_number: '',
    notes: '',
    ship_by: 'UPS', // Default to UPS
  });  
  const [newProduction, setNewProduction] = useState({ 
    job_id: '', 
    quantity_produced: '', 
    production_date: new Date().toISOString().split('T')[0], 
    shop_id: '', 
    machine_id: '' 
  });
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);
  const [isProductionModalOpen, setIsProductionModalOpen] = useState(false);
  const [isPOModalOpen, setIsPOModalOpen] = useState(false);
  const [poModalMode, setPoModalMode] = useState('create');
  const [poNumber, setPoNumber] = useState('');
  const [poDate, setPoDate] = useState(new Date().toISOString().split('T')[0]);
  const [lineItems, setLineItems] = useState([]);
  const [newLineItem, setNewLineItem] = useState({
    part_number: '',
    description: '',
    quantity_ordered: '',
    unit_price: '',
    expected_delivery_date: '',
    shop_id: '',
  });
  const [newLineItemSearch, setNewLineItemSearch] = useState('');
  const [showPartDropdown, setShowPartDropdown] = useState(false);

  const [error, setError] = useState('');
  const [customers, setCustomers] = useState([]);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [parts, setParts] = useState([]);
  const [isGeneratingPackingSlip, setIsGeneratingPackingSlip] = useState(false);
  const [jobFiles, setJobFiles] = useState([]); // State for job files
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false); // State for file upload modal
  const [isFilePreviewModalOpen, setIsFilePreviewModalOpen] = useState(false); // State for file preview modal
  const [previewFileUrl, setPreviewFileUrl] = useState(null); // State for file preview URL
  const [fileToUpload, setFileToUpload] = useState(null); // State for file to upload
  const [fileType, setFileType] = useState(''); // State for file type
  const [userRole, setUserRole] = useState(''); // State for user role
  const [jobNotes, setJobNotes] = useState(jobsData[0]?.notes || ''); // State for job notes
const [isNotesEditMode, setIsNotesEditMode] = useState(false); // State for notes edit mode
const [isMarkClosedModalOpen, setIsMarkClosedModalOpen] = useState(false);
const [selectedJobToClose, setSelectedJobToClose] = useState(null);
const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'default' });
const [materialDrawerOpen, setMaterialDrawerOpen] = useState(false);
const [selectedJobForMaterial, setSelectedJobForMaterial] = useState(null);

  // Fetch user profile data and role on mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase
          .from('users')
          .select('first_name, last_name, email, role')
          .eq('id', user.id) // Now both IDs are UUIDs
          .single();
        if (error) throw error;
        if (data) {
          setUserRole(data.role || '');
          console.log('User role:', data.role);
        }
      } catch (err) {
        console.error('Error fetching user profile:', err.message);
        console.log('Failed to fetch user profile.');
      }
    };

    fetchUserProfile();
  }, [user]);

  useEffect(() => {
    const fetchJobDetails = async () => {
      if (!user) return;

      setLoading(true);
      setIsKanbanOnly(false); // Reset on po_number change
      try {
        // Fetch jobs data first
        const { data: jobs, error: jobsError } = await supabase
          .from('jobs')
          .select(`
            id, po_number, po_line_number, part_number, revision, description,
            expected_delivery_date, quantity_on_order, unit_price, shop_id,
            quantity_shipped, status, estimated_running_days, po_start_date,
            customer_id, produced_quantity, shipped_quantity, high_priority, kanban_job_id, notes,
            material_status, material_notes,
            shops(id, name),
            customers(
              name, nickname,
              billing_address, billing_city, billing_state, billing_zip,
              billing_contact_name, billing_contact_email, billing_contact_phone,
              shipping_address, shipping_city, shipping_state, shipping_zip,
              shipping_contact_name, shipping_contact_email, shipping_contact_phone
            ),
            parts!inner(part_number, part_number_master, revision, inventory_quantity),
            kanban_jobs(po_number)
          `)
          .eq('po_number', po_number)
          .order('po_line_number');
        if (jobsError) throw new Error(`Jobs fetch error: ${jobsError.message}`);

        console.log('[Job.js DEBUG] po_number:', po_number);
        console.log('[Job.js DEBUG] jobs query result:', jobs);
        console.log('[Job.js DEBUG] jobs count:', jobs?.length || 0);

        // If no jobs found, check if this is a kanban PO
        if (!jobs || jobs.length === 0) {
          console.log('[Job.js DEBUG] No jobs found, checking kanban_jobs...');
          const { data: kanbanJobs, error: kanbanError } = await supabase
            .from('kanban_jobs')
            .select('id')
            .eq('po_number', po_number)
            .limit(1);

          console.log('[Job.js DEBUG] kanban_jobs result:', kanbanJobs);

          if (!kanbanError && kanbanJobs && kanbanJobs.length > 0) {
            // This is a kanban-only PO, render KanbanJob component
            console.log('[Job.js DEBUG] Setting isKanbanOnly = true');
            setIsKanbanOnly(true);
            setLoading(false);
            return;
          }
          console.log('[Job.js DEBUG] Not found in kanban_jobs either');
        }
    
        const partNumbers = jobs.map(job => job.part_number);
        const { data: partsData, error: partsError } = await supabase
          .from('parts')
          .select('part_number, part_number_master, revision, description')
          .in('part_number', partNumbers);
        if (partsError) throw new Error(`Parts fetch error: ${partsError.message}`);
    
        const partDescriptions = {};
        partsData.forEach(part => {
          partDescriptions[part.part_number] = part.description || '';
        });
    
        const updatedJobs = jobs.map(job => ({
          ...job,
          description: partDescriptions[job.part_number] || job.description || '',
        }));
    
        setJobsData(updatedJobs || []);
        setFilteredJobs(updatedJobs || []);
    
        // Check if the job is a Kanban work order after setting jobsData
        const hasKanbanJobId = updatedJobs.some(job => job.kanban_job_id !== null);
        setIsKanban(hasKanbanJobId);
        console.log('Is Kanban:', hasKanbanJobId);
    
        const jobIds = updatedJobs.map(job => job.id);
        const { data: deliveries, error: deliveriesError } = await supabase
          .from('deliveries')
          .select(`
            package_number, job_id, po_number, po_line_number, part_number, 
            delivery_number, quantity, ship_date, tracking_number, notes, packing_slip_path, ship_by
          `)
          .in('job_id', jobIds)
          .order('job_id, delivery_number');
        if (deliveriesError) throw new Error(`Deliveries fetch error: ${deliveriesError.message}`);
        setDeliveriesData(deliveries || []);
    
        const { data: production, error: productionError } = await supabase
          .from('production_logs')
          .select('log_id, job_id, part_number, quantity_produced, production_date, shop_id, machine_id, shops(id, name), machines(id, name)')
          .in('job_id', jobIds)
          .order('production_date', { ascending: false });
        if (productionError) throw new Error(`Production logs fetch error: ${productionError.message}`);
        setProductionLogs(production || []);
    
        const { data: jobFilesData, error: jobFilesError } = await supabase
          .from('job_files')
          .select('id, po_number, file_name, file_path, file_type, uploaded_at')
          .eq('po_number', po_number);
        if (jobFilesError) throw new Error(`Job files fetch error: ${jobFilesError.message}`);
        setJobFiles(jobFilesData || []);
    
        const { data: shopsData, error: shopsError } = await supabase
          .from('shops')
          .select('id, name');
        if (shopsError) throw new Error(`Shops fetch error: ${shopsError.message}`);
        console.log('Fetched shops:', shopsData);
        setShops(shopsData || []);
        if (!shopsData || shopsData.length === 0) {
          toast.warn('No shops found. Please add shops to the database.');
        }
    
        const { data: machinesData, error: machinesError } = await supabase
          .from('machines')
          .select('id, name');
        if (machinesError) throw new Error(`Machines fetch error: ${machinesError.message}`);
        console.log('Fetched machines:', machinesData);
        setMachines(machinesData || []);
        if (!machinesData || machinesData.length === 0) {
          toast.warn('No machines found. Please add machines to the database.');
        }
    
        const { data: customersData, error: customersError } = await supabase
          .from('customers')
          .select(`
            id, name, nickname, email, phone,
            billing_address, billing_city, billing_state, billing_zip,
            shipping_address, shipping_city, shipping_state, shipping_zip
          `);
        if (customersError) throw new Error(`Customers fetch error: ${customersError.message}`);
        setCustomers(customersData || []);
    
        // Fetch all parts for the search functionality
        const { data: allPartsData, error: allPartsError } = await supabase
          .from('parts')
          .select('part_number, part_number_master, revision, description');
        if (allPartsError) throw new Error(`Parts fetch error: ${allPartsError.message}`);
        setParts(allPartsData || []);
      } catch (err) {
        console.error('Error fetching job details:', err.message);
        toast.error(`Failed to fetch job details: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
  
    fetchJobDetails();
  }, [user, po_number, navigate]);


  useEffect(() => {
    if (jobsData.length > 0) {
      document.title = jobsData[0]?.po_number || 'Purchase Order';
    } else {
      document.title = 'Loading...';
    }
  }, [jobsData]);


  // Add refreshJobsData function
  const refreshJobsData = async () => {
    try {
      const { data: refreshedJobsData, error: fetchError } = await supabase
        .from('jobs')
        .select(`
          id, po_number, po_line_number, part_number, revision, description,
          expected_delivery_date, quantity_on_order, unit_price, shop_id,
          quantity_shipped, status, estimated_running_days, po_start_date,
          customer_id, produced_quantity, shipped_quantity, high_priority, kanban_job_id, notes,
          material_status, material_notes,
          shops(id, name),
          customers(
            name, nickname,
            billing_address, billing_city, billing_state, billing_zip, 
            billing_contact_name, billing_contact_email, billing_contact_phone,
            shipping_address, shipping_city, shipping_state, shipping_zip,
            shipping_contact_name, shipping_contact_email, shipping_contact_phone,
            net_terms
          ),
          parts!inner(part_number, inventory_quantity, part_number_master, revision)
        `)
        .eq('po_number', po_number)
        .order('po_line_number');
      if (fetchError) throw new Error(`Failed to fetch updated jobs: ${fetchError.message}`);

      setJobsData(refreshedJobsData || []);
      setFilteredJobs(refreshedJobsData || []);
    } catch (err) {
      console.error('Error refreshing jobs data:', err);
      toast.error(`Failed to refresh jobs data: ${err.message}`);
    }
  };


  const handleSaveNotes = async () => {
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ notes: jobNotes })
        .eq('po_number', po_number);
      if (error) throw new Error(`Failed to save notes: ${error.message}`);
  
      setIsNotesEditMode(false);
      toast.success('Notes saved successfully!');
    } catch (err) {
      console.error('Error saving notes:', err);
      toast.error(`Failed to save notes: ${err.message}`);
    }
  };

  

const handleAddProduction = async () => {
    if (
      !newProduction.job_id ||
      !newProduction.quantity_produced ||
      !newProduction.production_date ||
      !newProduction.shop_id ||
      !newProduction.machine_id
    ) {
      alert('Please fill in all production fields.');
      return;
    }
    try {
      const { data, error } = await supabase
        .from('production_logs')
        .insert({
          job_id: parseInt(newProduction.job_id),
          part_number: jobsData.find(j => j.id === parseInt(newProduction.job_id)).part_number,
          quantity_produced: parseInt(newProduction.quantity_produced),
          production_date: newProduction.production_date,
          shop_id: parseInt(newProduction.shop_id),
          machine_id: parseInt(newProduction.machine_id),
        })
        .select();
      if (error) throw error;
  
      const newLog = {
        ...data[0],
        shops: { name: shops.find(s => s.id === parseInt(newProduction.shop_id))?.name },
        machines: { name: machines.find(m => m.id === parseInt(newProduction.machine_id))?.name },
      };
      setProductionLogs(prevLogs => [...prevLogs, newLog]);
  
      // Refresh jobs data to reflect the updated status
      const { data: refreshedJobsData, error: fetchError } = await supabase
        .from('jobs')
        .select(`
          id, po_number, po_line_number, part_number, revision, description,
          expected_delivery_date, quantity_on_order, unit_price, shop_id,
          quantity_shipped, status, estimated_running_days, po_start_date,
          customer_id, produced_quantity, shipped_quantity, notes, material_status, material_notes,
          shops(id, name),
          customers(
            name, nickname,
            billing_address, billing_city, billing_state, billing_zip, 
            billing_contact_name, billing_contact_email, billing_contact_phone,
            shipping_address, shipping_city, shipping_state, shipping_zip,
            shipping_contact_name, shipping_contact_email, shipping_contact_phone
          ),
          parts!inner(part_number, inventory_quantity)
        `)
        .eq('po_number', po_number)
        .order('po_line_number');
      if (fetchError) throw new Error(`Failed to fetch updated jobs: ${fetchError.message}`);
  
      setJobsData(refreshedJobsData || []);
      setFilteredJobs(refreshedJobsData || []);
  
      setNewProduction({
        job_id: '',
        quantity_produced: '',
        production_date: new Date().toISOString().split('T')[0],
        shop_id: '',
        machine_id: '',
      });
      setIsProductionModalOpen(false);
    } catch (err) {
      console.error('Error adding production log:', err);
      toast.error(`Failed to add production log: ${err.message}`);
    }
  };

  const updateJobField = (jobId, field, value) => {
    setJobsData(jobsData.map(job =>
      job.id === jobId ? { ...job, [field]: field === 'quantity_shipped' ? parseInt(value) : value } : job
    ));
    setFilteredJobs(filteredJobs.map(job =>
      job.id === jobId ? { ...job, [field]: field === 'quantity_shipped' ? parseInt(value) : value } : job
    ));
  };

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  
    const sortedJobs = [...filteredJobs].sort((a, b) => {
      let aValue = a[field] || '';
      let bValue = b[field] || '';
      if (field === 'produced') {
        aValue = a.produced_quantity;
        bValue = b.produced_quantity;
      } else if (field === 'shipped') {
        aValue = a.shipped_quantity;
        bValue = b.shipped_quantity;
      } else if (field === 'remaining') {
        aValue = a.quantity_on_order - a.shipped_quantity;
        bValue = b.quantity_on_order - b.shipped_quantity;
      } else if (field === 'inventory') {
        aValue = a.parts?.inventory_quantity || 0;
        bValue = b.parts?.inventory_quantity || 0;
      } else if (field === 'needed') {
        aValue = Math.max(0, a.quantity_on_order - (a.parts?.inventory_quantity || 0) - a.produced_quantity);
        bValue = Math.max(0, b.quantity_on_order - (b.parts?.inventory_quantity || 0) - b.produced_quantity);
      }
      if (field === 'expected_delivery_date' || field === 'po_start_date') {
        const aDate = aValue ? new Date(aValue) : new Date(0);
        const bDate = bValue ? new Date(bValue) : new Date(0);
        return newDirection === 'asc' ? aDate - bDate : bDate - aDate;
      }
      if (typeof aValue === 'number') return newDirection === 'asc' ? aValue - bValue : bValue - aValue;
      return newDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
    setFilteredJobs(sortedJobs);
  };

  const handleFilter = () => {
    let filtered = [...jobsData];
    if (searchTerm) {
      filtered = filtered.filter(job =>
        job.part_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filterStatus) {
      filtered = filtered.filter(job => job.status === filterStatus);
    }
    setFilteredJobs(filtered);
  };

  useEffect(() => {
    handleFilter();
  }, [searchTerm, filterStatus]);

  const getStatusChipClass = (status) => {
    switch (status) {
      case 'not_started':
        return 'bg-red-100 text-red-800';
      case 'in_production':
        return 'bg-yellow-100 text-yellow-800';
      case 'partially_shipped':
        return 'bg-blue-100 text-blue-800';
      case 'ready_for_shipment':
        return 'bg-purple-100 text-purple-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const addDeliveryItem = () => {
    setNewDelivery(prev => ({
      ...prev,
      items: [...prev.items, { job_id: '', quantity: '' }],
    }));
  };

  const updateDeliveryItem = (index, field, value) => {
    const updatedItems = newDelivery.items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setNewDelivery(prev => ({ ...prev, items: updatedItems }));
  };

  const removeDeliveryItem = (index) => {
    setNewDelivery(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleViewPackingSlip = async (filePath) => {
    try {
      const { data, error } = await supabase.storage
        .from('delivery-files')
        .createSignedUrl(filePath, 60);
      if (error) throw new Error(`Failed to generate view URL: ${error.message}`);
      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error viewing packing slip:', err);
      toast.error(`Failed to view packing slip: ${err.message}`);
    }
  };

  const handleDownloadPackingSlip = async (filePath) => {
    try {
      const { data, error } = await supabase.storage
        .from('delivery-files')
        .download(filePath);
      if (error) throw new Error(`Failed to download packing slip: ${error.message}`);

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filePath.split('/').pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Packing slip downloaded successfully!');
    } catch (err) {
      console.error('Error downloading packing slip:', err);
      toast.error(`Failed to download packing slip: ${err.message}`);
    }
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { part_number: '', description: '', quantity_ordered: '', unit_price: '', expected_delivery_date: '', searchTerm: '' }]);
  };

  const updateLineItem = (index, field, value) => {
    const updatedItems = lineItems.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setLineItems(updatedItems);
  };

  const removeLineItem = (index) => {
    if (lineItems.length === 1) return;
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const handleSavePO = async () => {
    if (
      !poNumber ||
      !poDate ||
      lineItems.some(
        (item) =>
          !item.part_number ||
          !item.quantity_ordered ||
          !item.unit_price ||
          !item.expected_delivery_date
      )
    ) {
      setError('Please fill in all required fields for each line item.');
      return;
    }
  
    try {
      // Check and insert new part numbers into the parts table if they don't exist
      const partNumbers = [...new Set(lineItems.map(item => item.part_number))]; // Get unique part numbers
      const { data: existingParts, error: partsError } = await supabase
        .from('parts')
        .select('part_number')
        .in('part_number', partNumbers);
      if (partsError) throw new Error(`Failed to fetch parts: ${partsError.message}`);
  
      const existingPartNumbers = existingParts.map(part => part.part_number);
      const newPartNumbers = partNumbers.filter(partNumber => !existingPartNumbers.includes(partNumber));
  
      if (newPartNumbers.length > 0) {
        const newParts = newPartNumbers.map(partNumber => {
          const lineItem = lineItems.find(item => item.part_number === partNumber);
          return {
            part_number_master: partNumber.split('-').slice(0, -1).join('-'), // Extract part_number_master from part_number
            revision: partNumber.split('-').pop() || '0', // Extract revision or default to '0'
            description: lineItem?.description || '',
            inventory_quantity: 0, // Default inventory quantity
          };
        });
        const { error: insertPartsError } = await supabase
          .from('parts')
          .insert(newParts);
        if (insertPartsError) throw new Error(`Failed to insert new parts: ${insertPartsError.message}`);
      }
  
      if (poModalMode === 'create') {
        const jobsToInsert = lineItems.map((item, index) => ({
          po_number: poNumber,
          po_line_number: index + 1,
          part_number: item.part_number,
          description: item.description || '',
          quantity_on_order: parseInt(item.quantity_ordered, 10),
          unit_price: parseFloat(item.unit_price),
          expected_delivery_date: item.expected_delivery_date,
          shop_id: item.shop_id ? parseInt(item.shop_id) : null, // Include shop_id
          po_start_date: poDate,
          status: 'not_started',
          customer_id: jobsData[0]?.customer_id || null,
          produced_quantity: 0,
          shipped_quantity: 0,
        }));
  
        const { error } = await supabase.from('jobs').insert(jobsToInsert);
        if (error) throw new Error(`Failed to create PO: ${error.message}`);
  
        toast.success('Purchase Order created successfully!');
      } else {
        // Fetch the current jobs from the database to get the correct customer_id
        const { data: currentJobs, error: fetchJobsError } = await supabase
          .from('jobs')
          .select('id, customer_id, shop_id, shipped_quantity, produced_quantity, part_number')
          .eq('po_number', po_number); // Use the original po_number from useParams
        if (fetchJobsError) throw new Error(`Failed to fetch current jobs: ${fetchJobsError.message}`);
  
        // Ensure all jobs have the same customer_id
        const customerId = currentJobs[0]?.customer_id || null;
        if (currentJobs.some(job => job.customer_id !== customerId)) {
          console.warn('Inconsistent customer_id values detected for jobs in PO:', po_number);
        }
  
        const updatedJobs = lineItems.map((item) => {
          const oldJob = currentJobs.find((j) => j.id === item.job_id);
          const shipped = oldJob ? oldJob.shipped_quantity : 0;
          const produced = oldJob ? oldJob.produced_quantity : 0;
          const inventory = oldJob ? (parts.find(p => p.part_number === oldJob.part_number)?.inventory_quantity || 0) : 0;
          const quantityOnOrder = parseInt(item.quantity_ordered, 10);
          const status = determineStatus(shipped, produced, inventory, quantityOnOrder);
          return {
            id: oldJob?.id,
            po_number: poNumber, // Use the new poNumber
            po_line_number: item.po_line_number || oldJob?.po_line_number,
            part_number: item.part_number,
            description: item.description || '',
            quantity_on_order: quantityOnOrder,
            unit_price: parseFloat(item.unit_price),
            expected_delivery_date: item.expected_delivery_date,
            shop_id: item.shop_id ? parseInt(item.shop_id) : null, // Include shop_id
            po_start_date: poDate,
            status,
            customer_id: customerId, // Use the fetched customer_id
          };
        });

        // Delete jobs FIRST to avoid unique constraint violations when renumbering
        const updatedJobIds = updatedJobs.map((u) => u.id).filter((id) => !!id);
        const jobsToDelete = currentJobs.filter((j) => !updatedJobIds.includes(j.id));
        for (const job of jobsToDelete) {
          const { error: deleteLogsError } = await supabase
            .from('production_logs')
            .delete()
            .eq('job_id', job.id);
          if (deleteLogsError) throw new Error(`Failed to delete production logs for job ${job.id}: ${deleteLogsError.message}`);

          const { error: deleteJobError } = await supabase.from('jobs').delete().eq('id', job.id);
          if (deleteJobError) throw new Error(`Failed to delete job ${job.id}: ${deleteJobError.message}`);
        }

        // Now update/insert remaining jobs with new po_line_numbers
        for (const row of updatedJobs) {
          if (row.id) {
            const { error: updateError } = await supabase
              .from('jobs')
              .update({
                po_number: row.po_number, // Update po_number
                po_line_number: row.po_line_number,
                part_number: row.part_number,
                description: row.description,
                quantity_on_order: row.quantity_on_order,
                unit_price: row.unit_price,
                expected_delivery_date: row.expected_delivery_date,
                shop_id: row.shop_id, // Update shop_id
                po_start_date: row.po_start_date,
                status: row.status,
                customer_id: row.customer_id, // Ensure customer_id is preserved
              })
              .eq('id', row.id);
            if (updateError) throw new Error(`Failed to update job ${row.id}: ${updateError.message}`);
          } else {
            const { error: insertError } = await supabase.from('jobs').insert(row);
            if (insertError) throw new Error(`Failed to insert new job: ${insertError.message}`);
          }
        }

        const { data: updatedJobsData, error: fetchError } = await supabase
          .from('jobs')
          .select(`
            id, po_number, po_line_number, part_number, revision, description,
            expected_delivery_date, quantity_on_order, unit_price, shop_id,
            quantity_shipped, status, estimated_running_days, po_start_date,
            customer_id, produced_quantity, shipped_quantity, notes, material_status, material_notes,
            shops(id, name),
            customers(
              name, nickname,
              billing_address, billing_city, billing_state, billing_zip, 
              billing_contact_name, billing_contact_email, billing_contact_phone,
              shipping_address, shipping_city, shipping_state, shipping_zip,
              shipping_contact_name, shipping_contact_email, shipping_contact_phone
            ),
            parts!inner(part_number, part_number_master, revision, inventory_quantity)
          `)
          .eq('po_number', poNumber) // Use the new poNumber to fetch updated jobs
          .order('po_line_number');
        if (fetchError) throw new Error(`Failed to fetch updated jobs: ${fetchError.message}`);
  
        setJobsData(updatedJobsData || []);
        setFilteredJobs(updatedJobsData || []);
        toast.success('Purchase Order updated successfully!');
  
        // Redirect to the new PO URL
        navigate(`/jobs/${poNumber}`);
      }
  
      setPoNumber('');
      setPoDate(new Date().toISOString().split('T')[0]);
      setLineItems([
        {
          job_id: null,
          part_number: '',
          description: '',
          quantity_ordered: '',
          unit_price: '',
          expected_delivery_date: '',
          searchTerm: '',
          shop_id: '',
        },
      ]);
      setError('');
      setIsPOModalOpen(false);
    } catch (err) {
      console.error('Error saving PO:', err);
      setError(`Failed to save PO: ${err.message}`);
    }
  };

  const handleEditPO = () => {
    setPoNumber(po_number);
    setPoDate(jobsData[0]?.po_start_date || new Date().toISOString().split('T')[0]);
    setLineItems(
      jobsData.map(job => ({
        job_id: job.id,
        po_line_number: job.po_line_number,
        part_number: job.part_number,
        description: job.description || '',
        quantity_ordered: job.quantity_on_order.toString(),
        unit_price: job.unit_price?.toString() || '',
        expected_delivery_date: job.expected_delivery_date || '',
        shop_id: job.shop_id || '',
      }))
    );
    // Reset new line item form
    setNewLineItem({
      part_number: '',
      description: '',
      quantity_ordered: '',
      unit_price: '',
      expected_delivery_date: '',
      shop_id: '',
    });
    setNewLineItemSearch('');
    setPoModalMode('edit');
    setIsPOModalOpen(true);
  };

  const calculateShippedPercentage = (shipped, quantityOnOrder) => {
    if (quantityOnOrder === 0) return 0;
    return Math.min((shipped / quantityOnOrder) * 100, 100);
  };

  const calculateProgressPercentage = (shipped, produced, inventory, quantityOnOrder) => {
    if (quantityOnOrder === 0) return 0;
    const totalAvailable = shipped + (inventory || 0);
    return Math.min((totalAvailable / quantityOnOrder) * 100, 100);
  };

  const determineStatus = (shipped, produced, inventory, quantityOnOrder) => {
    if (quantityOnOrder === 0) return 'not_started';
    const shippedPercentage = calculateShippedPercentage(shipped, quantityOnOrder);
    const progressPercentage = calculateProgressPercentage(shipped, produced, inventory, quantityOnOrder);
    if (shippedPercentage === 100) return 'closed';
    if (progressPercentage === 0) return 'not_started';
    if (shipped > 0 && shippedPercentage < 100) return 'partially_shipped';
    if (progressPercentage >= 100) return 'ready_for_shipment';
    if (produced > 0 && shipped === 0) return 'in_production';
    return 'not_started';
  };

  const handleDeleteProductionLog = (logId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Production Log',
      message: 'Are you sure you want to delete this production log?',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const { error: deleteError } = await supabase
            .from('production_logs')
            .delete()
            .eq('log_id', logId);
          if (deleteError) throw new Error(`Failed to delete production log: ${deleteError.message}`);

          setProductionLogs(productionLogs.filter(log => log.log_id !== logId));

          const { data: refreshedJobsData, error: fetchError } = await supabase
            .from('jobs')
            .select(`
              id, po_number, po_line_number, part_number, revision, description,
              expected_delivery_date, quantity_on_order, unit_price, shop_id,
              quantity_shipped, status, estimated_running_days, po_start_date,
              customer_id, produced_quantity, shipped_quantity, notes, material_status, material_notes,
              shops(id, name),
              customers(
                name, nickname,
                billing_address, billing_city, billing_state, billing_zip,
                billing_contact_name, billing_contact_email, billing_contact_phone,
                shipping_address, shipping_city, shipping_state, shipping_zip,
                shipping_contact_name, shipping_contact_email, shipping_contact_phone
              ),
              parts!inner(part_number, inventory_quantity, part_number_master)
            `)
            .eq('po_number', po_number)
            .order('po_line_number');
          if (fetchError) throw new Error(`Failed to fetch updated jobs: ${fetchError.message}`);

          setJobsData(refreshedJobsData || []);
          setFilteredJobs(refreshedJobsData || []);
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'default' });
          toast.success('Production log deleted successfully!');
        } catch (err) {
          console.error('Error deleting production log:', err);
          toast.error(`Failed to delete production log: ${err.message}`);
        }
      }
    });
  };

  const handleDeleteDelivery = (delivery) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Delivery',
      message: `Are you sure you want to delete this delivery (Package #${delivery.package_number}, ${delivery.quantity} x ${delivery.part_number})?`,
      variant: 'danger',
      onConfirm: async () => {
        try {
          const { error: deleteError } = await supabase
            .from('deliveries')
            .delete()
            .eq('job_id', delivery.job_id)
            .eq('delivery_number', delivery.delivery_number);
          if (deleteError) throw new Error(`Failed to delete delivery: ${deleteError.message}`);

          setDeliveriesData(deliveriesData.filter(d =>
            !(d.job_id === delivery.job_id && d.delivery_number === delivery.delivery_number)
          ));

          const { data: refreshedJobsData, error: fetchError } = await supabase
            .from('jobs')
            .select(`
              id, po_number, po_line_number, part_number, revision, description,
              expected_delivery_date, quantity_on_order, unit_price, shop_id,
              quantity_shipped, status, estimated_running_days, po_start_date,
              customer_id, produced_quantity, shipped_quantity, notes, material_status, material_notes,
              shops(id, name),
              customers(
                name, nickname,
                billing_address, billing_city, billing_state, billing_zip,
                billing_contact_name, billing_contact_email, billing_contact_phone,
                shipping_address, shipping_city, shipping_state, shipping_zip,
                shipping_contact_name, shipping_contact_email, shipping_contact_phone
              ),
              parts!inner(part_number, inventory_quantity, part_number_master)
            `)
            .eq('po_number', po_number)
            .order('po_line_number');
          if (fetchError) throw new Error(`Failed to fetch updated jobs: ${fetchError.message}`);

          setJobsData(refreshedJobsData || []);
          setFilteredJobs(refreshedJobsData || []);
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'default' });
          toast.success('Delivery deleted successfully!');
        } catch (err) {
          console.error('Error deleting delivery:', err);
          toast.error(`Failed to delete delivery: ${err.message}`);
        }
      }
    });
  };

  const handleSelectCustomer = async (customerId) => {
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ customer_id: customerId })
        .eq('po_number', po_number);
      if (error) throw new Error(`Failed to update customer: ${error.message}`);
  
      const { data, error: fetchError } = await supabase
        .from('jobs')
        .select(`
          id, po_number, po_line_number, part_number, revision, description, 
          expected_delivery_date, quantity_on_order, unit_price, shop_id, 
          quantity_shipped, status, estimated_running_days, po_start_date, 
          customer_id, produced_quantity, shipped_quantity, notes, material_status, material_notes,
          shops(id, name), customers(name, shipping_address, shipping_city, shipping_state, shipping_zip),
          parts!inner(part_number, inventory_quantity)
        `)
        .eq('po_number', po_number)
        .order('po_line_number');
      if (fetchError) throw new Error(`Failed to fetch updated jobs: ${fetchError.message}`);
  
      setJobsData(data || []);
      setFilteredJobs(data || []);
      setIsCustomerModalOpen(false);
      setCustomerSearchTerm('');
      toast.success('Customer updated successfully!');
    } catch (err) {
      console.error('Error updating customer:', err);
      toast.error(`Failed to update customer: ${err.message}`);
    }
  };

  // Add functions to handle part selection and creation
const handlePartSelect = (index, part) => {
  const updatedItems = lineItems.map((item, i) =>
    i === index ? { ...item, part_number: part.part_number, description: part.description, searchTerm: '' } : item
  );
  setLineItems(updatedItems);
};

const handleCreatePart = async (index) => {
  const searchTerm = lineItems[index].searchTerm;
  if (!searchTerm) return;
  const newPart = {
    part_number_master: searchTerm,
    revision: '0', // Default revision
    description: `New Part: ${searchTerm}`,
    inventory_quantity: 0,
  };
  try {
    const { data, error } = await supabase
      .from('parts')
      .insert(newPart)
      .select('part_number, description')
      .single();
    if (error) throw error;
    setParts([...parts, data]);
    handlePartSelect(index, data);
  } catch (err) {
    console.error('Error creating part:', err);
    setError('Failed to create new part: ' + err.message);
  }
};

const updateLineItemMultiple = (index, updates) => {
  setLineItems(prev =>
    prev.map((item, i) =>
      i === index ? { ...item, ...updates } : item
    )
  );
};

const handleDeletePO = () => {
  setConfirmModal({
    isOpen: true,
    title: 'Delete Purchase Order',
    message: 'Are you sure you want to delete this PO? This will permanently delete all production logs and delivery records. This action cannot be undone.',
    variant: 'destructive',
    onConfirm: async () => {
      try {
        const jobIds = jobsData.map(job => job.id);

        const { error: deleteLogsError } = await supabase
          .from('production_logs')
          .delete()
          .in('job_id', jobIds);
        if (deleteLogsError) throw new Error(`Failed to delete production logs: ${deleteLogsError.message}`);

        const { error: deleteDeliveriesError } = await supabase
          .from('deliveries')
          .delete()
          .in('job_id', jobIds);
        if (deleteDeliveriesError) throw new Error(`Failed to delete deliveries: ${deleteDeliveriesError.message}`);

        const { error: deleteJobsError } = await supabase
          .from('jobs')
          .delete()
          .eq('po_number', po_number);
        if (deleteJobsError) throw new Error(`Failed to delete jobs: ${deleteJobsError.message}`);

        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'default' });
        toast.success('Purchase Order deleted successfully!');
        navigate('/jobs');
      } catch (err) {
        console.error('Error deleting PO:', err);
        toast.error(`Failed to delete PO: ${err.message}`);
      }
    }
  });
};

const handleFileUpload = async () => {
  if (!fileToUpload || !fileType) {
    toast.error('Please select a file and file type.');
    return;
  }

  try {
    const filePath = `job-files/${po_number}/${fileToUpload.name}`;
    const { error: uploadError } = await supabase.storage
      .from('job-files')
      .upload(filePath, fileToUpload);
    if (uploadError) throw new Error(`Failed to upload file: ${uploadError.message}`);

    const { data, error: insertError } = await supabase
      .from('job_files')
      .insert({
        po_number,
        file_name: fileToUpload.name,
        file_path: filePath,
        file_type: fileType,
        uploaded_by: user.id,
      })
      .select();
    if (insertError) throw new Error(`Failed to insert file record: ${insertError.message}`);

    setJobFiles([...jobFiles, data[0]]);
    setFileToUpload(null);
    setFileType('');
    setIsFileUploadModalOpen(false);
    toast.success('File uploaded successfully!');
  } catch (err) {
    console.error('Error uploading file:', err);
    toast.error(`Failed to upload file: ${err.message}`);
  }
};

const handleFileDownload = async (filePath) => {
  try {
    const { data, error } = await supabase.storage
      .from('job-files')
      .download(filePath);
    if (error) throw new Error(`Failed to download file: ${error.message}`);

    const url = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filePath.split('/').pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('File downloaded successfully!');
  } catch (err) {
    console.error('Error downloading file:', err);
    toast.error(`Failed to download file: ${err.message}`);
  }
};

const handleFilePreview = async (filePath) => {
  try {
    const { data, error } = await supabase.storage
      .from('job-files')
      .createSignedUrl(filePath, 60); // URL valid for 60 seconds
    if (error) throw new Error(`Failed to generate signed URL: ${error.message}`);

    // Open the file in a new tab
    window.open(data.signedUrl, '_blank');

    // Refresh the current tab to reset state (if needed)
    window.location.reload();
  } catch (err) {
    console.error('Error generating preview URL:', err);
    toast.error(`Failed to preview file: ${err.message}`);
  }
};


const handleFileDelete = (fileId, filePath) => {
  setConfirmModal({
    isOpen: true,
    title: 'Delete File',
    message: 'Are you sure you want to delete this file?',
    variant: 'danger',
    onConfirm: async () => {
      try {
        const { error: deleteError } = await supabase.storage
          .from('job-files')
          .remove([filePath]);
        if (deleteError) throw new Error(`Failed to delete file from storage: ${deleteError.message}`);

        const { error: dbError } = await supabase
          .from('job_files')
          .delete()
          .eq('id', fileId);
        if (dbError) throw new Error(`Failed to delete file record: ${dbError.message}`);

        setJobFiles(jobFiles.filter(file => file.id !== fileId));
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'default' });
        toast.success('File deleted successfully!');
      } catch (err) {
        console.error('Error deleting file:', err);
        toast.error(`Failed to delete file: ${err.message}`);
      }
    }
  });
};

// Add these state variables near your other useState declarations
const [isHighPriorityModalOpen, setIsHighPriorityModalOpen] = useState(false);

// Function to toggle the high_priority flag for a given job
const flagHighPriority = async (job) => {
  const newPriority = job.high_priority ? 0 : 1;
  try {
    const { error } = await supabase
      .from('jobs')
      .update({ high_priority: newPriority })
      .eq('id', job.id);
    if (error) throw error;
    // Update jobsData state with the new high_priority flag
    setJobsData((prevJobs) =>
      prevJobs.map((j) => (j.id === job.id ? { ...j, high_priority: newPriority } : j))
    );
    toast.success(
      `Job ${job.po_line_number} ${newPriority ? 'flagged as High Priority' : 'removed from High Priority'}.`
    );
  } catch (err) {
    console.error('Error updating high priority:', err);
    toast.error('Failed to update high priority.');
  }
};

const handleMarkAsClosed = (job) => {
  setConfirmModal({
    isOpen: true,
    title: 'Mark as Closed',
    message: `Are you sure you want to mark ${job.po_number} as closed? This action cannot be undone.`,
    variant: 'warning',
    onConfirm: async () => {
      try {
        const { error } = await supabase
          .from('jobs')
          .update({ status: 'closed' })
          .eq('id', job.id);
        if (error) throw new Error(`Failed to mark job as closed: ${error.message}`);

        setJobsData(jobsData.map(j =>
          j.id === job.id ? { ...j, status: 'closed' } : j
        ));
        setFilteredJobs(filteredJobs.map(j =>
          j.id === job.id ? { ...j, status: 'closed' } : j
        ));

        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'default' });
        toast.success(`Work Order ${job.po_number} marked as closed successfully!`);
      } catch (err) {
        console.error('Error marking job as closed:', err);
        toast.error(`Failed to mark job as closed: ${err.message}`);
      }
    }
  });
};

const openMaterialDrawer = (job) => {
  setSelectedJobForMaterial(job);
  setMaterialDrawerOpen(true);
};

const handleMaterialUpdate = (updatedJob) => {
  const updateJob = (job) =>
    job.id === updatedJob.id
      ? { ...job, material_status: updatedJob.material_status, material_notes: updatedJob.material_notes }
      : job;

  setJobsData(prev => prev.map(updateJob));
  setFilteredJobs(prev => prev.map(updateJob));
};

useEffect(() => {
  if (jobsData.length > 0) {
    setJobNotes(jobsData[0]?.notes || '');
  }
}, [jobsData]);


  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <p className="text-slate-500">Loading...</p>
    </div>
  );

  // If this is a kanban-only PO (exists in kanban_jobs but not jobs), render KanbanJob
  if (isKanbanOnly) {
    return <KanbanJob />;
  }

  if (jobsData.length === 0) return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <p className="text-red-600">No jobs found for PO {po_number}</p>
    </div>
  );

  // Get kanban PO id for back link
  const parentKanbanPoNumber = isKanban ? jobsData.find(job => job.kanban_jobs !== null)?.kanban_jobs?.po_number : null;

  // Calculate totals for overview stats
  const totalOrdered = jobsData.reduce((sum, job) => sum + (job.quantity_on_order || 0), 0);
  const totalProduced = jobsData.reduce((sum, job) => sum + (job.produced_quantity || 0), 0);
  const totalShipped = jobsData.reduce((sum, job) => sum + (job.shipped_quantity || 0), 0);
  const overallProgress = totalOrdered > 0 ? Math.round((totalShipped / totalOrdered) * 100) : 0;

  return (
    <div className="p-4 bg-slate-50 min-h-screen">
      <div className="w-full mx-auto">
        {/* Unified Header */}
        <div className="bg-white rounded-sm border border-slate-200 mb-4">
          <div className="px-4 py-3 flex justify-between items-center border-b border-slate-100">
            <div className="flex items-center gap-2 text-sm">
              {isKanban ? (
                <>
                  <Link to={`/jobs/${parentKanbanPoNumber}`} className="text-slate-500 hover:text-slate-700 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    {parentKanbanPoNumber}
                  </Link>
                  <span className="text-slate-300">/</span>
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">Work Order</span>
                  <span className="font-semibold text-slate-800">#{po_number}</span>
                </>
              ) : (
                <>
                  <Link to="/standard-pos" className="text-slate-500 hover:text-slate-700 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Standard POs
                  </Link>
                  <span className="text-slate-300">/</span>
                  <span className="font-semibold text-slate-800">#{po_number}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isKanban && (
                <button
                  onClick={() => {
                    const jobToClose = jobsData.find(job => job.status !== 'closed');
                    if (jobToClose) {
                      handleMarkAsClosed(jobToClose);
                    } else {
                      toast.info('All work orders are already closed.');
                    }
                  }}
                  disabled={jobsData.every(job => job.status === 'closed')}
                  className="px-2.5 py-1.5 text-xs text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-50 hover:border-slate-400 transition-colors disabled:opacity-50"
                >
                  Mark Closed
                </button>
              )}
              {userRole !== 'worker' && (
                <>
                  <button
                    onClick={handleEditPO}
                    className="px-2.5 py-1.5 text-xs text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-50 hover:border-slate-400 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setIsHighPriorityModalOpen(true)}
                    className="px-2.5 py-1.5 text-xs text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-50 hover:border-slate-400 transition-colors"
                  >
                    Priority
                  </button>
                  <button
                    onClick={handleDeletePO}
                    className="px-2.5 py-1.5 text-xs text-red-500 border border-red-200 rounded-sm hover:bg-red-50 hover:border-red-300 transition-colors"
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Overview Stats */}
          <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <p className="text-xs text-slate-500">Customer</p>
              <p className="text-sm font-medium text-slate-800 truncate">{jobsData[0]?.customers?.name || 'No customer'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Line Items</p>
              <p className="text-sm font-medium text-slate-800">{jobsData.length}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Ordered</p>
              <p className="text-sm font-medium text-slate-800 tabular-nums">{totalOrdered.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Produced</p>
              <p className="text-sm font-medium text-slate-800 tabular-nums">{totalProduced.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Shipped</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 transition-all duration-300"
                    style={{ width: `${overallProgress}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-slate-800 tabular-nums">{overallProgress}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Line Items Section */}
        <div className="bg-white border border-slate-200 rounded-sm mb-4">
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Line Items</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Shop</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide cursor-pointer hover:bg-slate-100" onClick={() => handleSort('po_line_number')}>
                    Line {sortField === 'po_line_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3 h-3 inline" /> : <ChevronDownIcon className="w-3 h-3 inline" />)}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide cursor-pointer hover:bg-slate-100" onClick={() => handleSort('part_number')}>
                    Part Number {sortField === 'part_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3 h-3 inline" /> : <ChevronDownIcon className="w-3 h-3 inline" />)}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Description</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide cursor-pointer hover:bg-slate-100" onClick={() => handleSort('expected_delivery_date')}>
                    Due {sortField === 'expected_delivery_date' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3 h-3 inline" /> : <ChevronDownIcon className="w-3 h-3 inline" />)}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide cursor-pointer hover:bg-slate-100" onClick={() => handleSort('status')}>
                    Status {sortField === 'status' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3 h-3 inline" /> : <ChevronDownIcon className="w-3 h-3 inline" />)}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Progress</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Shipped</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide cursor-pointer hover:bg-slate-100" onClick={() => handleSort('quantity_on_order')}>
                    Ord {sortField === 'quantity_on_order' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3 h-3 inline" /> : <ChevronDownIcon className="w-3 h-3 inline" />)}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide cursor-pointer hover:bg-slate-100" onClick={() => handleSort('inventory')}>
                    Inv {sortField === 'inventory' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3 h-3 inline" /> : <ChevronDownIcon className="w-3 h-3 inline" />)}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide cursor-pointer hover:bg-slate-100" onClick={() => handleSort('produced')}>
                    Prod {sortField === 'produced' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3 h-3 inline" /> : <ChevronDownIcon className="w-3 h-3 inline" />)}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide cursor-pointer hover:bg-slate-100" onClick={() => handleSort('shipped')}>
                    Ship {sortField === 'shipped' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3 h-3 inline" /> : <ChevronDownIcon className="w-3 h-3 inline" />)}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Material</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {jobsData.map((job) => {
                  const produced = job.produced_quantity;
                  const shipped = job.shipped_quantity;
                  const inventory = job.parts?.inventory_quantity || 0;
                  const shippedPercentage = calculateShippedPercentage(shipped, job.quantity_on_order);
                  const progressPercentage = calculateProgressPercentage(shipped, produced, inventory, job.quantity_on_order);
                  return (
                    <tr key={job.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{job.shops?.name || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">{job.po_line_number}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                        <Link
                          to={`/part/${job.part_number}`}
                          className="font-medium text-slate-800 hover:text-blue-600 transition-colors"
                        >
                          {job.part_number}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-sm text-slate-600 truncate max-w-xs" title={job.description}>{job.description || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">
                        {job.expected_delivery_date ? new Date(job.expected_delivery_date).toLocaleDateString() : 'KANBAN'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-sm ${getStatusChipClass(job.status)}`}>
                          {job.status.replace('_', ' ').replace(/\b\w/g, char => char.toUpperCase())}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-slate-600 transition-all" style={{ width: `${progressPercentage}%` }}></div>
                          </div>
                          <span className="text-xs text-slate-500 tabular-nums w-8">{progressPercentage.toFixed(0)}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-green-600 transition-all" style={{ width: `${shippedPercentage}%` }}></div>
                          </div>
                          <span className="text-xs text-slate-500 tabular-nums w-8">{shippedPercentage.toFixed(0)}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">{job.quantity_on_order}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">{inventory}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">{produced}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">
                        {!isKanban && editMode === job.id ? (
                          <input
                            type="number"
                            value={job.quantity_shipped}
                            onChange={(e) => updateJobField(job.id, 'quantity_shipped', e.target.value)}
                            className="w-16 px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                            min="0"
                            max={job.quantity_on_order}
                            autoFocus
                          />
                        ) : (
                          shipped
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs">
                        <button
                          onClick={() => openMaterialDrawer(job)}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm font-medium cursor-pointer transition-colors ${
                            job.material_status === 'no_stock'
                              ? 'bg-red-100 text-red-700 hover:bg-red-200'
                              : job.material_status === 'ordered'
                              ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                              : job.material_status === 'ready'
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                          }`}
                          title={job.material_notes || 'Click to edit material info'}
                        >
                          {job.material_status === 'no_stock' ? (
                            <>
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                              No Stock
                            </>
                          ) : job.material_status === 'ordered' ? (
                            <>
                              <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
                              Ordered
                            </>
                          ) : job.material_status === 'ready' ? (
                            <>
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                              Ready
                            </>
                          ) : (
                            '—'
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add Production Modal */}
        <Transition appear show={isProductionModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setIsProductionModalOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                      <Dialog.Title className="text-sm font-semibold text-slate-800">Add Production Log</Dialog.Title>
                      <button onClick={() => setIsProductionModalOpen(false)} className="text-slate-400 hover:text-slate-600">×</button>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">Job</label>
                          <select
                            value={newProduction.job_id}
                            onChange={(e) => setNewProduction({ ...newProduction, job_id: e.target.value })}
                            className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
                          >
                            <option value="">Select Job</option>
                            {jobsData.map(job => (
                              <option key={job.id} value={job.id}>{job.po_line_number} - {job.part_number}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">Quantity</label>
                          <input
                            type="number"
                            value={newProduction.quantity_produced}
                            onChange={(e) => setNewProduction({ ...newProduction, quantity_produced: e.target.value })}
                            className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                            min="0"
                            placeholder="Enter quantity"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">Date</label>
                          <input
                            type="date"
                            value={newProduction.production_date}
                            onChange={(e) => setNewProduction({ ...newProduction, production_date: e.target.value })}
                            className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">Shop</label>
                          <select
                            value={newProduction.shop_id}
                            onChange={(e) => setNewProduction({ ...newProduction, shop_id: e.target.value })}
                            className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
                          >
                            <option value="">Select Shop</option>
                            {shops.map(shop => (
                              <option key={shop.id} value={shop.id}>{shop.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">Machine</label>
                          <select
                            value={newProduction.machine_id}
                            onChange={(e) => setNewProduction({ ...newProduction, machine_id: e.target.value })}
                            className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
                          >
                            <option value="">Select Machine</option>
                            {machines.map(machine => (
                              <option key={machine.id} value={machine.id}>{machine.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50">
                      <button
                        onClick={() => {
                          setNewProduction({ job_id: '', quantity_produced: '', production_date: new Date().toISOString().split('T')[0], shop_id: '', machine_id: '' });
                          setIsProductionModalOpen(false);
                        }}
                        className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-sm transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAddProduction}
                        className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                      >
                        Save
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Production Logs Section */}
        <div className="bg-white border border-slate-200 rounded-sm mb-4">
          <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Production Logs</h2>
            <button
              onClick={() => {
                setNewProduction({ ...newProduction, job_id: jobsData[0]?.id || '' });
                setIsProductionModalOpen(true);
              }}
              className="px-2.5 py-1 text-xs text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-50 hover:border-slate-400 transition-colors"
            >
              Add Production
            </button>
          </div>
          {productionLogs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Line</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Part Number</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Qty</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Shop</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Machine</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {productionLogs.map((log) => {
                    const job = jobsData.find(j => j.id === log.job_id);
                    const uniqueKey = `${log.log_id}-${log.job_id}-${log.production_date}`;
                    return (
                      <tr key={uniqueKey} className="hover:bg-slate-50 transition-colors">
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">{job?.po_line_number || '-'}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700 font-medium">{log.part_number}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">{log.quantity_produced}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">{new Date(log.production_date).toLocaleDateString('en-US', { timeZone: 'UTC' })}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{log.shops?.name || '-'}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{log.machines?.name || '-'}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          <button
                            onClick={() => handleDeleteProductionLog(log.log_id)}
                            className="text-xs text-slate-400 hover:text-red-600 transition-colors"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="px-3 py-4 text-sm text-slate-500">No production logs recorded.</p>
          )}
        </div>

        {/* Deliveries Section */}
        <div className="bg-white border border-slate-200 rounded-sm mb-4">
          <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Deliveries</h2>
            <button
              onClick={() => {
                setNewDelivery({ items: [{ job_id: jobsData[0]?.id || '', quantity: '' }], ship_date: new Date().toISOString().split('T')[0], tracking_number: '', notes: '' });
                setIsDeliveryModalOpen(true);
              }}
              className="px-2.5 py-1 text-xs text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-50 hover:border-slate-400 transition-colors"
            >
              New Delivery
            </button>
          </div>
          {deliveriesData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Pkg</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Line</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Part Number</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Qty</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Ship Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Ship By</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Tracking</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Notes</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide"></th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {deliveriesData.map((delivery) => (
                    <tr key={`${delivery.package_number}-${delivery.delivery_number}`} className="hover:bg-slate-50 transition-colors">
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">{delivery.package_number}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">{delivery.po_line_number}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700 font-medium">{delivery.part_number}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">{delivery.quantity}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">{new Date(delivery.ship_date).toLocaleDateString('en-US', { timeZone: 'UTC' })}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{delivery.ship_by || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{delivery.tracking_number || '-'}</td>
                      <td className="px-3 py-2 text-sm text-slate-600 truncate max-w-xs" title={delivery.notes}>{delivery.notes || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                        {delivery.packing_slip_path ? (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleViewPackingSlip(delivery.packing_slip_path)}
                              className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleDownloadPackingSlip(delivery.packing_slip_path)}
                              className="text-xs text-blue-600 hover:text-blue-700 transition-colors"
                            >
                              Download
                            </button>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleDeleteDelivery(delivery)}
                          className="text-xs text-red-500 hover:text-red-700 transition-colors"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="px-3 py-4 text-sm text-slate-500">No deliveries recorded.</p>
          )}
        </div>

        {/* Files Section */}
        <div className="bg-white border border-slate-200 rounded-sm mb-4">
          <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Files</h2>
            <button
              onClick={() => setIsFileUploadModalOpen(true)}
              className="px-2.5 py-1 text-xs text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-50 hover:border-slate-400 transition-colors"
            >
              Upload
            </button>
          </div>
          {jobFiles.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">File Name</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Type</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wide"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {jobFiles.map((file) => (
                    <tr key={file.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700 font-medium">{file.file_name}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{file.file_type}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">{new Date(file.uploaded_at).toLocaleDateString()}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleFilePreview(file.file_path)}
                            className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                          >
                            Preview
                          </button>
                          <button
                            onClick={() => handleFileDownload(file.file_path)}
                            className="text-xs text-blue-600 hover:text-blue-700 transition-colors"
                          >
                            Download
                          </button>
                          <button
                            onClick={() => handleFileDelete(file.id, file.file_path)}
                            className="text-xs text-slate-400 hover:text-red-600 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="px-3 py-4 text-sm text-slate-500">No files uploaded.</p>
          )}
        </div>

        {/* Customer Section */}
        <div className="bg-white border border-slate-200 rounded-sm mb-4">
          <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Customer</h2>
            {userRole !== 'worker' && !isKanban && (
              <button
                onClick={() => setIsCustomerModalOpen(true)}
                className="px-2.5 py-1 text-xs text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-50 hover:border-slate-400 transition-colors"
              >
                {jobsData[0]?.customers ? 'Change' : 'Select'}
              </button>
            )}
          </div>
          {jobsData.length > 0 && jobsData[0].customers ? (
            <div className="px-3 py-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                {jobsData[0].customers.nickname && (
                  <p className="text-sm text-slate-600">
                    <span className="font-medium text-slate-700">Nickname:</span> {jobsData[0].customers.nickname}
                  </p>
                )}
                <p className="text-sm text-slate-600">
                  <span className="font-medium text-slate-700">Name:</span> {jobsData[0].customers.name}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-medium text-slate-700">Ship To:</span> {jobsData[0].customers.shipping_address || 'N/A'}, {jobsData[0].customers.shipping_city || 'N/A'}, {jobsData[0].customers.shipping_state || 'N/A'} {jobsData[0].customers.shipping_zip || 'N/A'}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-slate-600">
                  <span className="font-medium text-slate-700">Email:</span> {jobsData[0].customers.shipping_contact_email || 'N/A'}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-medium text-slate-700">Phone:</span> {jobsData[0].customers.shipping_contact_phone || 'N/A'}
                </p>
              </div>
            </div>
          ) : (
            <p className="px-3 py-4 text-sm text-slate-500">No customer assigned.</p>
          )}
        </div>

        {/* Notes Section */}
        <div className="bg-white border border-slate-200 rounded-sm mb-4">
          <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Notes</h2>
            <button
              onClick={() => {
                if (isNotesEditMode) {
                  handleSaveNotes();
                } else {
                  setIsNotesEditMode(true);
                }
              }}
              className={`px-2.5 py-1 text-xs rounded-sm transition-colors ${
                isNotesEditMode
                  ? 'text-green-600 border border-green-300 hover:bg-green-50 hover:border-green-400'
                  : 'text-slate-600 border border-slate-300 hover:bg-slate-50 hover:border-slate-400'
              }`}
            >
              {isNotesEditMode ? 'Save' : 'Edit'}
            </button>
          </div>
          <div className="px-3 py-3">
            {isNotesEditMode ? (
              <textarea
                value={jobNotes}
                onChange={(e) => setJobNotes(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                rows="4"
                placeholder="Enter notes here..."
              />
            ) : (
              <p className="text-sm text-slate-600 whitespace-pre-wrap">
                {jobNotes || 'No notes.'}
              </p>
            )}
          </div>
        </div>

        {/* File Upload Modal */}
        <Transition appear show={isFileUploadModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setIsFileUploadModalOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                      <Dialog.Title className="text-sm font-semibold text-slate-800">Upload File</Dialog.Title>
                      <button onClick={() => setIsFileUploadModalOpen(false)} className="text-slate-400 hover:text-slate-600">×</button>
                    </div>
                    <div className="p-4 space-y-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">File</label>
                        <input
                          type="file"
                          onChange={(e) => setFileToUpload(e.target.files[0])}
                          className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">File Type</label>
                        <select
                          value={fileType}
                          onChange={(e) => setFileType(e.target.value)}
                          className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
                        >
                          <option value="">Select Type</option>
                          <option value="Drawing">Drawing</option>
                          <option value="PO">PO</option>
                          <option value="Material Invoice">Material Invoice</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50">
                      <button
                        onClick={() => {
                          setFileToUpload(null);
                          setFileType('');
                          setIsFileUploadModalOpen(false);
                        }}
                        className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-sm transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleFileUpload}
                        className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                      >
                        Upload
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* File Preview Modal */}
        <Transition appear show={isFilePreviewModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setIsFilePreviewModalOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-[90vw] h-[90vh] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] transform rounded-sm bg-white shadow-xl transition-all">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                      <Dialog.Title className="text-sm font-semibold text-slate-800">File Preview</Dialog.Title>
                      <button onClick={() => setIsFilePreviewModalOpen(false)} className="text-slate-400 hover:text-slate-600">×</button>
                    </div>
                    <div className="h-[calc(100%-7rem)] overflow-auto p-4">
                      {previewFileUrl ? (
                        <iframe
                          src={previewFileUrl}
                          className="w-full h-full border border-slate-200 rounded-sm"
                          title="File Preview"
                        />
                      ) : (
                        <p className="text-slate-500">Unable to load file.</p>
                      )}
                    </div>
                    <div className="flex justify-end px-4 py-3 border-t border-slate-200 bg-slate-50">
                      <button
                        onClick={() => setIsFilePreviewModalOpen(false)}
                        className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>


        {/* Create/Edit PO Modal */}
        <Transition appear show={isPOModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setIsPOModalOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                      <Dialog.Title className="text-sm font-semibold text-slate-800">
                        {poModalMode === 'create' ? 'Create PO' : 'Edit PO'}
                      </Dialog.Title>
                      <button onClick={() => setIsPOModalOpen(false)} className="text-slate-400 hover:text-slate-600">×</button>
                    </div>

                    <div className="p-4 max-h-[70vh] overflow-y-auto">
                      {error && <p className="mb-4 px-3 py-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm">{error}</p>}

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">PO Number</label>
                          <input
                            type="text"
                            value={poNumber}
                            onChange={(e) => setPoNumber(e.target.value)}
                            className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">PO Date</label>
                          <input
                            type="date"
                            value={poDate}
                            onChange={(e) => setPoDate(e.target.value)}
                            className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                            required
                          />
                        </div>
                      </div>

                      {/* Add New Line Item Section */}
                      <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-sm">
                        <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">Add Line Item</h3>
                        <div className="grid grid-cols-6 gap-3">
                          <div className="col-span-2 relative">
                            <label className="block text-xs text-slate-500 mb-1">Part Number</label>
                            <input
                              type="text"
                              value={newLineItemSearch}
                              onChange={(e) => {
                                setNewLineItemSearch(e.target.value);
                                setShowPartDropdown(true);
                              }}
                              onFocus={() => setShowPartDropdown(true)}
                              onBlur={() => setTimeout(() => setShowPartDropdown(false), 150)}
                              className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                              placeholder="Search parts..."
                            />
                            {showPartDropdown && newLineItemSearch && (
                              <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-48 overflow-y-auto">
                                {parts.filter(part =>
                                  part.part_number.toLowerCase().includes(newLineItemSearch.toLowerCase())
                                ).length > 0 ? (
                                  parts
                                    .filter(part => part.part_number.toLowerCase().includes(newLineItemSearch.toLowerCase()))
                                    .slice(0, 10)
                                    .map((part) => (
                                      <div
                                        key={part.part_number}
                                        onClick={() => {
                                          setNewLineItem({
                                            ...newLineItem,
                                            part_number: part.part_number,
                                            description: part.description,
                                            unit_price: part.unit_price || '',
                                          });
                                          setNewLineItemSearch(part.part_number);
                                          setShowPartDropdown(false);
                                        }}
                                        className="px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer"
                                      >
                                        <span className="font-medium text-slate-800">{part.part_number}</span>
                                        <span className="text-slate-500 ml-2">{part.description}</span>
                                      </div>
                                    ))
                                ) : (
                                  <div
                                    onClick={() => {
                                      setNewLineItem({
                                        ...newLineItem,
                                        part_number: newLineItemSearch,
                                        description: '',
                                      });
                                      setShowPartDropdown(false);
                                    }}
                                    className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 cursor-pointer"
                                  >
                                    Create new part: "{newLineItemSearch}"
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs text-slate-500 mb-1">Qty</label>
                            <input
                              type="number"
                              value={newLineItem.quantity_ordered}
                              onChange={(e) => setNewLineItem({ ...newLineItem, quantity_ordered: e.target.value })}
                              className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums"
                              min="0"
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-slate-500 mb-1">Price</label>
                            <input
                              type="number"
                              value={newLineItem.unit_price}
                              onChange={(e) => setNewLineItem({ ...newLineItem, unit_price: e.target.value })}
                              className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-slate-500 mb-1">Due Date</label>
                            <input
                              type="date"
                              value={newLineItem.expected_delivery_date}
                              onChange={(e) => setNewLineItem({ ...newLineItem, expected_delivery_date: e.target.value })}
                              className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-slate-500 mb-1">Shop</label>
                            <select
                              value={newLineItem.shop_id}
                              onChange={(e) => setNewLineItem({ ...newLineItem, shop_id: e.target.value })}
                              className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
                            >
                              <option value="">Select</option>
                              {shops.map((shop) => (
                                <option key={shop.id} value={shop.id}>{shop.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        {newLineItem.part_number && (
                          <p className="mt-2 text-xs text-slate-500">
                            Selected: <span className="font-medium text-slate-700">{newLineItem.part_number}</span>
                            {newLineItem.description && <span> - {newLineItem.description}</span>}
                          </p>
                        )}
                        <div className="mt-3 flex justify-end">
                          <button
                            onClick={() => {
                              if (newLineItem.part_number && newLineItem.quantity_ordered) {
                                setLineItems([...lineItems, {
                                  ...newLineItem,
                                  searchTerm: ''
                                }]);
                                setNewLineItem({
                                  part_number: '',
                                  description: '',
                                  quantity_ordered: '',
                                  unit_price: '',
                                  expected_delivery_date: '',
                                  shop_id: '',
                                });
                                setNewLineItemSearch('');
                              } else {
                                toast.error('Please select a part and enter quantity');
                              }
                            }}
                            className="px-3 py-1.5 text-xs bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                          >
                            Add Line Item
                          </button>
                        </div>
                      </div>

                      {/* Line Items Table */}
                      <div className="mb-4">
                        <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Line Items ({lineItems.length})</h3>
                        {lineItems.length > 0 ? (
                          <div className="border border-slate-200 rounded-sm overflow-x-auto">
                            <table className="w-full">
                              <thead>
                                <tr className="bg-slate-50 border-b border-slate-200">
                                  <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide w-14">Line</th>
                                  <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Part Number</th>
                                  <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">Description</th>
                                  <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide w-20">Qty</th>
                                  <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide w-24">Price</th>
                                  <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide w-28">Due</th>
                                  <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide w-24">Shop</th>
                                  <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wide w-16"></th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {lineItems.map((item, index) => (
                                  <tr key={index}>
                                    <td className="px-3 py-2">
                                      <input
                                        type="number"
                                        value={item.po_line_number || ''}
                                        onChange={(e) => updateLineItem(index, 'po_line_number', parseInt(e.target.value) || '')}
                                        className="w-full px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums"
                                        min="1"
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-sm font-medium text-slate-800">{item.part_number}</td>
                                    <td className="px-3 py-2 text-sm text-slate-600 truncate max-w-xs">{item.description}</td>
                                    <td className="px-3 py-2">
                                      <input
                                        type="number"
                                        value={item.quantity_ordered}
                                        onChange={(e) => updateLineItem(index, 'quantity_ordered', e.target.value)}
                                        className="w-full px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums"
                                        min="0"
                                      />
                                    </td>
                                    <td className="px-3 py-2">
                                      <input
                                        type="number"
                                        value={item.unit_price}
                                        onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                                        className="w-full px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums"
                                        min="0"
                                        step="0.01"
                                      />
                                    </td>
                                    <td className="px-3 py-2">
                                      <input
                                        type="date"
                                        value={item.expected_delivery_date}
                                        onChange={(e) => updateLineItem(index, 'expected_delivery_date', e.target.value)}
                                        className="w-full px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                                      />
                                    </td>
                                    <td className="px-3 py-2">
                                      <select
                                        value={item.shop_id}
                                        onChange={(e) => updateLineItem(index, 'shop_id', e.target.value)}
                                        className="w-full px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
                                      >
                                        <option value="">-</option>
                                        {shops.map((shop) => (
                                          <option key={shop.id} value={shop.id}>{shop.name}</option>
                                        ))}
                                      </select>
                                    </td>
                                    <td className="px-3 py-2">
                                      <button
                                        onClick={() => removeLineItem(index)}
                                        className="text-xs text-slate-400 hover:text-red-600 transition-colors"
                                      >
                                        Remove
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500 py-4 text-center border border-dashed border-slate-200 rounded-sm">
                            No line items added. Use the form above to add items.
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50">
                      <button
                        onClick={() => setIsPOModalOpen(false)}
                        className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-sm transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSavePO}
                        className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                      >
                        {poModalMode === 'create' ? 'Save PO' : 'Update PO'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Select Customer Modal */}
        <Transition appear show={isCustomerModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setIsCustomerModalOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                      <Dialog.Title className="text-sm font-semibold text-slate-800">Select Customer</Dialog.Title>
                      <button onClick={() => setIsCustomerModalOpen(false)} className="text-slate-400 hover:text-slate-600">×</button>
                    </div>

                    <div className="p-4">
                      <input
                        type="text"
                        value={customerSearchTerm}
                        onChange={(e) => setCustomerSearchTerm(e.target.value)}
                        placeholder="Search by name, email, or phone..."
                        className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 mb-4"
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-80 overflow-y-auto">
                        {customers
                          .filter(customer =>
                            customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
                            (customer.email && customer.email.toLowerCase().includes(customerSearchTerm.toLowerCase())) ||
                            (customer.phone && customer.phone.toLowerCase().includes(customerSearchTerm.toLowerCase()))
                          )
                          .map(customer => (
                            <div
                              key={customer.id}
                              onClick={() => handleSelectCustomer(customer.id)}
                              className="p-3 border border-slate-200 rounded-sm hover:bg-slate-50 hover:border-slate-300 cursor-pointer transition-colors"
                            >
                              <h3 className="text-sm font-semibold text-slate-800">{customer.name}</h3>
                              {customer.nickname && (
                                <p className="text-xs text-slate-500">{customer.nickname}</p>
                              )}
                              <p className="text-xs text-slate-500 mt-1 truncate">
                                {customer.shipping_address || 'N/A'}, {customer.shipping_city || ''} {customer.shipping_state || ''}
                              </p>
                              <p className="text-xs text-slate-500 truncate">{customer.email || customer.phone || 'No contact'}</p>
                            </div>
                          ))}
                      </div>
                    </div>

                    <div className="flex justify-end px-4 py-3 border-t border-slate-200 bg-slate-50">
                      <button
                        onClick={() => setIsCustomerModalOpen(false)}
                        className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-sm transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* High Priority Modal */}
        {isHighPriorityModalOpen && (
          <Transition appear show={isHighPriorityModalOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setIsHighPriorityModalOpen(false)}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-black/30" />
              </Transition.Child>
              <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                        <Dialog.Title className="text-sm font-semibold text-slate-800">High Priority</Dialog.Title>
                        <button onClick={() => setIsHighPriorityModalOpen(false)} className="text-slate-400 hover:text-slate-600">×</button>
                      </div>
                      <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
                        {jobsData.map((job) => (
                          <div key={job.id} className="flex justify-between items-center p-3 border border-slate-200 rounded-sm hover:bg-slate-50">
                            <div>
                              <p className="text-sm font-medium text-slate-800">Line {job.po_line_number}</p>
                              <p className="text-xs text-slate-500">{job.part_number} - {job.description}</p>
                            </div>
                            <button
                              onClick={() => flagHighPriority(job)}
                              className={`px-3 py-1 text-xs rounded-sm transition-colors ${
                                job.high_priority
                                  ? 'bg-red-600 text-white hover:bg-red-700'
                                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                              }`}
                            >
                              {job.high_priority ? 'Remove Priority' : 'Set Priority'}
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-end px-4 py-3 border-t border-slate-200 bg-slate-50">
                        <button
                          onClick={() => setIsHighPriorityModalOpen(false)}
                          className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                        >
                          Done
                        </button>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition>
        )}

        {/* Confirmation Modal */}
        <Transition appear show={confirmModal.isOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                    <div className={`flex items-center justify-between px-4 py-3 border-b ${
                      confirmModal.variant === 'destructive' ? 'bg-red-50 border-red-100' :
                      confirmModal.variant === 'danger' ? 'bg-red-50 border-red-100' :
                      confirmModal.variant === 'warning' ? 'bg-amber-50 border-amber-100' :
                      'bg-slate-50 border-slate-200'
                    }`}>
                      <Dialog.Title className={`text-sm font-semibold ${
                        confirmModal.variant === 'destructive' || confirmModal.variant === 'danger' ? 'text-red-800' :
                        confirmModal.variant === 'warning' ? 'text-amber-800' :
                        'text-slate-800'
                      }`}>
                        {confirmModal.title}
                      </Dialog.Title>
                      <button
                        onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                        className="text-slate-400 hover:text-slate-600"
                      >
                        ×
                      </button>
                    </div>
                    <div className="p-4">
                      <p className="text-sm text-slate-600">{confirmModal.message}</p>
                    </div>
                    <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50">
                      <button
                        onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                        className="px-3 py-1.5 text-sm text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-100 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={confirmModal.onConfirm}
                        className={`px-3 py-1.5 text-sm text-white rounded-sm transition-colors ${
                          confirmModal.variant === 'destructive' ? 'bg-red-600 hover:bg-red-700' :
                          confirmModal.variant === 'danger' ? 'bg-red-600 hover:bg-red-700' :
                          confirmModal.variant === 'warning' ? 'bg-amber-600 hover:bg-amber-700' :
                          'bg-slate-700 hover:bg-slate-800'
                        }`}
                      >
                        {confirmModal.variant === 'destructive' ? 'Delete' :
                         confirmModal.variant === 'danger' ? 'Delete' :
                         confirmModal.variant === 'warning' ? 'Confirm' :
                         'Confirm'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

<CreateDelivery
  isOpen={isDeliveryModalOpen}
  onClose={() => setIsDeliveryModalOpen(false)}
  jobsData={jobsData}
  poNumber={po_number}
  deliveriesData={deliveriesData}
  setDeliveriesData={setDeliveriesData}
  isKanban={false}
  usePartNumberMaster={true}
  onDeliveryCreated={refreshJobsData}
/>

      {/* Material Drawer */}
      <MaterialDrawer
        isOpen={materialDrawerOpen}
        onClose={() => setMaterialDrawerOpen(false)}
        job={selectedJobForMaterial}
        onUpdate={handleMaterialUpdate}
      />
      </div>
    </div>
  );
}

export default Job;